import {
  matchTemplates,
  type TemplateRegistryEntry,
} from '@/features/templates/registry'
import {
  parseContractReadback,
  type ContractReadback,
} from './contract-readback'
import {
  parseFieldSpecReadback,
  type FieldSpecReadback,
} from './field-spec-readback'
import {
  parseLegacyScreenReadback,
  type LegacyScreenReadback,
} from './legacy-screen-readback'
import {
  parseScreenDesignReadback,
  type ScreenDesignReadback,
} from './screen-design-readback'
import {
  parseReferenceExtractionReadback,
  type ReferenceExtractionReadback,
} from './reference-extraction-readback'

export type StudioCreationDecision =
  | {
      /**
       * The pasted text IS a machine contract (extracted server contract or
       * OpenAPI). S1 of the WonBanking scenarios doc: for this reader the
       * right first move is not generation but a read-back — "this is what I
       * read out of your document; is it right?" — so the surface renders
       * `readback` for confirmation before anything is created.
       */
      kind: 'contract-readback'
      readback: ContractReadback
      prompt: string
    }
  | {
      /**
       * The pasted text is a message-spec FIELD TABLE (Excel TSV / markdown).
       * S2 of the WonBanking scenarios doc: the list is decidable, but which
       * fields appear on screen versus travel internally is the human's one
       * question — the surface renders `readback` as a checkbox pass before
       * anything is created.
       */
      kind: 'field-spec-readback'
      readback: FieldSpecReadback
      prompt: string
    }
  | {
      /**
       * The pasted text is a LEGACY SCREEN SOURCE (JSP/HTML with scriptlets
       * or taglibs). S3 of the WonBanking scenarios doc: this is a
       * conversion, not a new build — reusing the new-build flow here fails.
       * The surface renders `readback` as a comparison view (what was read ↔
       * what conversion does) plus the carry-vs-improve difference list,
       * before anything is created.
       */
      kind: 'legacy-screen-readback'
      readback: LegacyScreenReadback
      prompt: string
    }
  | {
      /**
       * The pasted text is a SCREEN DESIGN DOCUMENT (areas, quoted labels,
       * flow lines). S4 of the WonBanking scenarios doc: the layout is
       * decidable and quoted labels are carried verbatim, but the document
       * usually does not say where the data comes from — the surface renders
       * `readback` and states the data gap, pointing at the S1/S2 lanes,
       * before anything is created.
       */
      kind: 'screen-design-readback'
      readback: ScreenDesignReadback
      prompt: string
    }
  | {
      /**
       * The paste combines a reference DOCUMENT with a "make it like this"
       * INTENT. S7 of the WonBanking scenarios doc: the required UI is
       * "extract the standard from the reference", never "clone the
       * reference" — the surface renders `readback` for the human's
       * standard-vs-screen-specific sorting before anything is created.
       */
      kind: 'reference-extraction-readback'
      readback: ReferenceExtractionReadback
      prompt: string
    }
  | {
      /**
       * The paste contains SEVERAL documents separated by an explicit `---`
       * line, and at least two of them are intake documents of DISTINCT
       * kinds (a contract + a field table, a field table + a legacy screen…).
       * W21 rung ④ — the bank developer holds more than one document, and
       * asking them to run the intake once per paste loses the composition.
       * Each part carries ITS OWN segment as `prompt`, so each card reads
       * back exactly the document it describes and the composite confirm
       * serializes every decision block into one generation prompt.
       *
       * Deliberately conservative: segmentation only engages on the explicit
       * separator, and duplicate kinds fall back to single-document routing
       * (the decision grammar's merge semantics for same-kind blocks are not
       * ratified — refusing is honest, silently dropping one is not).
       */
      kind: 'multi-intake-readback'
      parts: MultiIntakePart[]
      prompt: string
    }
  | {
      kind: 'template-proposal'
      templateId: string
      displayName: string
      prompt: string
      matchedTerms: string[]
    }
  | {
      kind: 'zero-base-generation'
      prompt: string
    }

export type MultiIntakePart =
  | { kind: 'contract-readback'; readback: ContractReadback; segment: string }
  | { kind: 'field-spec-readback'; readback: FieldSpecReadback; segment: string }
  | {
      kind: 'legacy-screen-readback'
      readback: LegacyScreenReadback
      segment: string
    }
  | {
      kind: 'screen-design-readback'
      readback: ScreenDesignReadback
      segment: string
    }
  | {
      kind: 'reference-extraction-readback'
      readback: ReferenceExtractionReadback
      segment: string
    }

/** Explicit document separator: a line of three-or-more dashes on its own. */
const DOCUMENT_SEPARATOR = /^[ \t]*-{3,}[ \t]*$/m

function routeSingleIntakeDocument(segment: string): MultiIntakePart | null {
  const contract = parseContractReadback(segment)
  if (contract) return { kind: 'contract-readback', readback: contract, segment }
  const fieldSpec = parseFieldSpecReadback(segment)
  if (fieldSpec) {
    return { kind: 'field-spec-readback', readback: fieldSpec, segment }
  }
  const reference = parseReferenceExtractionReadback(segment)
  if (reference) {
    return { kind: 'reference-extraction-readback', readback: reference, segment }
  }
  const legacy = parseLegacyScreenReadback(segment)
  if (legacy) {
    return { kind: 'legacy-screen-readback', readback: legacy, segment }
  }
  const design = parseScreenDesignReadback(segment)
  if (design) {
    return { kind: 'screen-design-readback', readback: design, segment }
  }
  return null
}

function routeMultiIntake(prompt: string): StudioCreationDecision | null {
  if (!DOCUMENT_SEPARATOR.test(prompt)) return null
  const segments = prompt
    .split(new RegExp(DOCUMENT_SEPARATOR.source, 'm'))
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0)
  if (segments.length < 2) return null
  const parts = segments
    .map((segment) => routeSingleIntakeDocument(segment))
    .filter((part): part is MultiIntakePart => part !== null)
  if (parts.length === 0) return null
  const kinds = new Set(parts.map((part) => part.kind))
  if (parts.length >= 2 && kinds.size === parts.length) {
    return { kind: 'multi-intake-readback', parts, prompt }
  }
  // Composition refused (one recognized document, or duplicate kinds whose
  // decision-block merge semantics are unratified) — degrade to the FIRST
  // recognized document's single card. The card's readback comes from its
  // segment, but the prompt stays the FULL paste: the whole-text parsers are
  // strict and would reject the mixed text outright (measured: the table +
  // prose paste fell to zero-base), which silently loses the document the
  // developer pasted. Only the first duplicate gets a checkbox pass; the
  // rest ride as text (documented limit).
  const first = parts[0]
  return {
    kind: first.kind,
    readback: first.readback,
    prompt,
  } as StudioCreationDecision
}

function matchedTemplateTerms(
  entry: TemplateRegistryEntry,
  prompt: string,
): string[] {
  const normalizedPrompt = prompt.normalize('NFC').toLowerCase()
  return (entry.pkg.terminologySurface?.terms ?? []).filter(
    (term) =>
      term.length > 0 &&
      normalizedPrompt.includes(term.normalize('NFC').toLowerCase()),
  )
}

export function routeStudioCreationRequest(
  prompt: string,
): StudioCreationDecision {
  // BEFORE single-document parsing: a multi-document paste (explicit `---`
  // separators, >= 2 distinct intake kinds) must not let the first parser
  // claim the whole text and silently drop the other documents.
  const multi = routeMultiIntake(prompt)
  if (multi) return multi

  // Checked before template matching: a pasted contract can contain any words
  // (a summary field mentioning "storefront" must not turn a contract into a
  // template proposal). Prose never parses as a contract, so existing prompts
  // are untouched.
  const readback = parseContractReadback(prompt)
  if (readback) {
    return { kind: 'contract-readback', readback, prompt }
  }

  // Same precedence reasoning for a pasted field table: a 설명 cell mentioning
  // a template term must not turn a spec into a template proposal. Both
  // parsers refuse prose, so ordinary prompts never reach these branches.
  const fieldSpec = parseFieldSpecReadback(prompt)
  if (fieldSpec) {
    return { kind: 'field-spec-readback', readback: fieldSpec, prompt }
  }

  // BEFORE the legacy/design lanes: a reference paste ("이것처럼" + screen)
  // also parses as a bare screen, and letting the legacy lane claim it would
  // offer to CONVERT the reference — the exact clone-shaped mistake S7 warns
  // about. Intent is the discriminator, and this parser requires it.
  const referenceExtraction = parseReferenceExtractionReadback(prompt)
  if (referenceExtraction) {
    return {
      kind: 'reference-extraction-readback',
      readback: referenceExtraction,
      prompt,
    }
  }

  // And for a pasted screen source: a legacy title or label containing a
  // template term must not turn a conversion into a new-build proposal.
  const legacyScreen = parseLegacyScreenReadback(prompt)
  if (legacyScreen) {
    return { kind: 'legacy-screen-readback', readback: legacyScreen, prompt }
  }

  // And a pasted design document: a quoted label containing a template term
  // must not turn a 설계서 into a template proposal.
  const screenDesign = parseScreenDesignReadback(prompt)
  if (screenDesign) {
    return { kind: 'screen-design-readback', readback: screenDesign, prompt }
  }

  const entry = matchTemplates(prompt)[0]
  if (!entry) {
    return {
      kind: 'zero-base-generation',
      prompt,
    }
  }

  return {
    kind: 'template-proposal',
    templateId: entry.pkg.templateId,
    displayName: entry.displayName,
    prompt,
    matchedTerms: matchedTemplateTerms(entry, prompt),
  }
}

// ─── Generation trigger provenance (2026-08-17, wave-4 lane-2) ───────────
//
// `cogo.studio.requestAppGeneration` is dispatched under `internal`
// authority (`mcpBus.execute`, studio-mcp-bus.ts) -- no confirmation card,
// no approval token (routing it through `.executeWebMcp()` would hit the
// registry-derived approval floor and permanently block generation, since
// no token is ever minted for this deterministic path -- see
// docs/superpowers/specs/2026-08-17-studio-slice4-disclosure-authority.md
// §6 P4 rows #7-#10). Two call sites had nothing pinning that a dispatch
// actually flowed through THIS classifier's read of the user's own typed
// prompt, as opposed to a model-authored, replayed, or bridge-injected
// payload landing on the same call: chat-pipeline-wiring.ts's
// `executeAppGenerationRequest`, and start-app-generation-from-prompt.ts's
// `startAppGenerationFromClassifiedPrompt` wrapper around the internal
// `mcpBus.execute` call.
//
// The proof is an opaque, unforgeable-from-JSON value: a plain object
// registered in a module-private WeakSet at mint time. A payload crossing
// any wire/tool-call boundary (what a model-authored, replayed, or
// bridge-injected caller can supply) can at best contain a STRING or a
// plain object shaped like `GenerationTriggerProvenance` -- it can never
// contain a live reference this WeakSet recognizes, so only genuine
// same-process code that imports `mintClassifierDispatchTrigger` from HERE
// (next to the classifier; not re-exported through any broader barrel) can
// produce a value that verifies.
const GENUINE_GENERATION_TRIGGERS = new WeakSet<object>()

/** Opaque -- do not construct directly. Use `mintClassifierDispatchTrigger`. */
export interface GenerationTriggerProvenance {
  readonly __brand: 'studio-creation-router.generation-trigger-provenance'
  readonly prompt: string
}

/**
 * Mint proof that a `requestAppGeneration` dispatch originates from THIS
 * classifier's read of the user's own typed prompt. Call immediately after
 * `routeStudioCreationRequest(prompt)` (or the equivalent in-chat
 * generation-readiness confirmation) has run for the SAME prompt, at the
 * call site that is about to start generation -- do not cache the result,
 * mint ahead of the classifier call, or pass it through indirection that
 * could let a different prompt's dispatch borrow it.
 */
export function mintClassifierDispatchTrigger(
  prompt: string,
): GenerationTriggerProvenance {
  const token: GenerationTriggerProvenance = {
    __brand: 'studio-creation-router.generation-trigger-provenance',
    prompt,
  }
  GENUINE_GENERATION_TRIGGERS.add(token)
  return token
}

/** True only for a value minted by `mintClassifierDispatchTrigger` in THIS module instance. */
export function isGenuineGenerationTrigger(
  value: unknown,
): value is GenerationTriggerProvenance {
  return (
    typeof value === 'object' &&
    value !== null &&
    GENUINE_GENERATION_TRIGGERS.has(value as object)
  )
}

/**
 * Distinct, fail-closed error for a `requestAppGeneration` dispatch that
 * cannot prove genuine classifier-dispatch provenance. Thrown BEFORE any
 * network or `mcpBus.execute` call -- a model-authored, replayed, or
 * bridge-injected payload can supply a string or a forged object shape but
 * never the live WeakSet membership `isGenuineGenerationTrigger` requires.
 */
export class GenerationTriggerProvenanceError extends Error {
  override readonly name = 'GenerationTriggerProvenanceError'
  readonly code: 'GENERATION_TRIGGER_MISSING' | 'GENERATION_TRIGGER_FORGED'

  constructor(code: 'GENERATION_TRIGGER_MISSING' | 'GENERATION_TRIGGER_FORGED') {
    super(
      code === 'GENERATION_TRIGGER_MISSING'
        ? 'App generation requires genuine classifier-dispatch provenance; none was supplied.'
        : 'App generation provenance failed verification; it was not minted by the deterministic classifier.',
    )
    this.code = code
  }
}

/**
 * Required-provenance assertion: throws `GenerationTriggerProvenanceError`
 * (`GENERATION_TRIGGER_MISSING` on an absent value, `GENERATION_TRIGGER_FORGED`
 * on anything present but not genuine) unless `value` was minted by
 * `mintClassifierDispatchTrigger`.
 */
export function assertGenuineGenerationTrigger(
  value: unknown,
): asserts value is GenerationTriggerProvenance {
  if (value === undefined) {
    throw new GenerationTriggerProvenanceError('GENERATION_TRIGGER_MISSING')
  }
  if (!isGenuineGenerationTrigger(value)) {
    throw new GenerationTriggerProvenanceError('GENERATION_TRIGGER_FORGED')
  }
}
